<template>
  <div ref="fullscreenContainer" class="fullscreen-player">
    <!-- Scene Selector Dialog -->
    <div v-if="showSceneSelector" class="scene-selector-overlay">
      <div class="scene-selector">
        <h3>选择起始章节</h3>
        <div class="scene-list">
          <div
            v-for="(scene, index) in scenes"
            :key="scene.id || index"
            class="scene-item"
            :class="{ active: selectedStartIndex === index }"
            @click="selectedStartIndex = index"
          >
            <span class="scene-index">{{ index + 1 }}</span>
            <span class="scene-name">{{ scene.name || getScenePreview(scene) }}</span>
          </div>
        </div>
        <div class="scene-selector-actions">
          <el-button @click="exitFullscreen">取消</el-button>
          <el-button type="primary" @click="startFromSelected">开始播放</el-button>
        </div>
      </div>
    </div>

    <StoryPlayPlayer
      v-if="scenes.length > 0 && !showSceneSelector"
      :scenes="scenes"
      :typing-speed="typingSpeed"
      :continue-space="continueSpace"
      :fade-duration="500"
      :start-scene-index="startSceneIndex"
    />
    <div v-else-if="!showSceneSelector" class="loading">加载中...</div>
    <div v-if="!showSceneSelector" class="exit-hint" @click="exitFullscreen">按 ESC 或点击此处退出</div>
  </div>
</template>

<script>
import StoryPlayPlayer from '@/components/StoryPlayPlayer.vue'
import { getStoryPlayData, getSystemSetting } from '@/net/api'

export default {
  name: 'StoryPlayFullscreen',
  components: {
    StoryPlayPlayer
  },
  data() {
    return {
      scenes: [],
      showSceneSelector: this.$route.query.skip_selector !== '1',
      selectedStartIndex: 0,
      startSceneIndex: 0,
      typingSpeed: 250,
      continueSpace: -1,
      exitingRoute: false
    }
  },
  created() {
    const storyId = this.$route.params.id
    if (storyId) {
      Promise.all([
        getStoryPlayData(storyId),
        getSystemSetting('story_typing_speed'),
        getSystemSetting('story_continue_space')
      ]).then(([res, typingRes, continueRes]) => {
        this.scenes = res.data.scenes || []
        // Convert chars/second to ms/char
        const charsPerSecond = parseInt(typingRes?.data?.value) || 4
        this.typingSpeed = Math.max(50, Math.min(1000, Math.round(1000 / charsPerSecond)))

        // Get story_continue_space
        const spaceVal = parseInt(continueRes?.data?.value)
        this.continueSpace = isNaN(spaceVal) ? -1 : spaceVal

        // 处理查询参数：支持直接跳过选择界面并指定起始场景
        const skipSelector = this.$route.query.skip_selector === '1'
        const startIndex = parseInt(this.$route.query.start) || 0
        if (skipSelector) {
          this.selectedStartIndex = startIndex
          this.startFromSelected()
        }
      }).catch(err => {
        console.error('Failed to load story play data:', err)
      })
    }
    this.enterFullscreen()
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.leaveFullscreenOnly()
  },
  methods: {
    getReturnRoute() {
      const returnTo = this.$route.query.return_to;
      const storyId = this.$route.params.id;
      if (returnTo === "edit" && storyId) {
        const query = {};
        if (this.$route.query.scene_id !== undefined) {
          query.scene_id = this.$route.query.scene_id;
        }
        return { path: `/story-plays/${storyId}/edit`, query };
      }
      return { path: "/story-plays" };
    },
    getScenePreview(scene) {
      const lines = scene.lines || []
      if (lines.length > 0) {
        const first = lines[0]
        const text = typeof first === 'string' ? first : (first && first.text) || ''
        if (text) {
          return text.slice(0, 20)
        }
      }
      return '（空场景）'
    },
    startFromSelected() {
      this.startSceneIndex = this.selectedStartIndex
      this.showSceneSelector = false
    },
    enterFullscreen() {
      const el = this.$refs.fullscreenContainer
      if (el) {
        if (el.requestFullscreen) {
          el.requestFullscreen()
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen()
        }
      }
    },
    leaveFullscreenOnly() {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    },
    navigateBack() {
      if (this.exitingRoute) return
      this.exitingRoute = true
      const target = this.getReturnRoute()
      const resolved = this.$router.resolve(target)
      if (resolved && resolved.route && resolved.route.fullPath === this.$route.fullPath) {
        this.exitingRoute = false
        return
      }
      this.$router.push(target).catch(() => {})
    },
    exitFullscreen() {
      this.leaveFullscreenOnly()
      this.navigateBack()
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.leaveFullscreenOnly()
        this.navigateBack()
      }
    }
  }
}
</script>

<style scoped>
.fullscreen-player {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 9999;
}

.loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
}

.exit-hint {
  position: absolute;
  top: 16px;
  right: 16px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  cursor: pointer;
  z-index: 100;
}

.exit-hint:hover {
  color: rgba(255, 255, 255, 0.8);
}

.scene-selector-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.scene-selector {
  background: #1a1a2e;
  border-radius: 8px;
  padding: 24px;
  width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.scene-selector h3 {
  color: #fff;
  margin: 0 0 16px 0;
  text-align: center;
}

.scene-list {
  flex: 1;
  overflow-y: auto;
  max-height: 50vh;
  margin-bottom: 16px;
}

.scene-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  color: #fff;
}

.scene-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.scene-item.active {
  background: #409eff;
}

.scene-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 12px;
  flex-shrink: 0;
}

.scene-item.active .scene-index {
  background: rgba(255, 255, 255, 0.3);
}

.scene-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scene-selector-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
